import { useCallback, useEffect, useState } from 'react';
import { getProfile, updateProfile, getCurrentPlan } from '../services/profile';
import type { Plan, Profile } from '../types';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Charge le profil + le plan, et expose une sauvegarde avec feedback explicite
 * (saving / saved / error) — l'écran ne doit jamais échouer en silence.
 */
export function useProfile() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [plan, setPlan] = useState<Plan>('free');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [p, pl] = await Promise.all([getProfile(), getCurrentPlan()]);
        if (!active) return;
        setProfile(p);
        setPlan(pl);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Chargement impossible.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const save = useCallback(async (patch: Partial<Profile>): Promise<boolean> => {
    setSaveState('saving');
    setSaveError(null);
    try {
      await updateProfile(patch);
      setProfile((prev) => (prev ? { ...prev, ...patch } : prev));
      setSaveState('saved');
      return true;
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Enregistrement impossible.');
      setSaveState('error');
      return false;
    }
  }, []);

  return { profile, plan, loading, error, save, saveState, saveError };
}
